import { BasicColumnTypes, IOgqlV1Column, SemanticTypes } from '@opengov/ogql-types';
import { EN_DASH } from '@opengov/reporting-formatters/formatters/accountSummaryFormatters';
import { ILocaleFormatter, IColumnSource, IRowSource, isCalculationExpression, isRowOverride } from '@opengov/reporting-types';
import type { ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';

export interface TableFormatterArgs {
  calculatedText: string;
  rowGroups: IRowSource[];
  formatter: ILocaleFormatter;
}

export type TableFormatter = (columnIndex: number, columnGroup: IColumnSource) => ValueFormatterFunc;

/**
 * Curried ag-grid value formatter for financial report cells.
 * Resolves the column's semantic type (currency, percent, number, date) and
 * applies locale-aware formatting. Returns an en-dash for placeholder rows
 * or when column metadata is unavailable.
 */
export const tableFormatter =
  (args: TableFormatterArgs) =>
  (columnIndex: number, columnGroup: IColumnSource) =>
  (params: ValueFormatterParams<any>): string => {
    const { formatter, calculatedText, rowGroups } = args;
    // Multi-hierarchy: blank expanded rows because totals appear in a child total row
    if (params?.data?.isLoadError || (params?.data?.hasTotalRow && params?.data?.canExpand && params?.node?.expanded)) {
      return '';
    }
    // Note that rowGroup will be undefined for a new row that has been applied but not saved
    const rowGroup = rowGroups[params.data?.rowGroupIndex];
    if (params?.data?.placeholder) {
      if ((!rowGroup || isRowOverride(rowGroup)) && isCalculationExpression(columnGroup)) {
        return calculatedText;
      }
      return EN_DASH;
    }
    // Avoid crashing when metadataColumns not updated yet after adding column group
    if (columnIndex >= params?.data?.groupData?.metadataColumns?.length) {
      return EN_DASH;
    }
    const type = isCalculationExpression(columnGroup)
      ? columnGroup?.display
      : columnSemanticTypeToFormatterType(params?.data?.groupData?.metadataColumns[columnIndex]);

    return formatter[type](params.value);
  };

const getAllSemanticTypes = (column: IOgqlV1Column) => {
  // Typed as required, but a response column can arrive without it — an ERP override row on a lens
  // whose schema was not resolved produced exactly that. Reading it unguarded threw inside an
  // ag-grid value formatter, which unmounts the entire report rather than degrading one cell.
  const semanticTypes = column?.semanticTypes;
  if (!semanticTypes) {
    return new Set<string>();
  }
  return new Set(semanticTypes.flatMap((t) => t));
};

const isNumericColumn = (column: IOgqlV1Column & { type?: string }) => {
  return (
    column?.semanticTypes &&
    (column.logicalType?.includes('logical/numeric') || column.type === BasicColumnTypes.Numeric)
  );
};

export const columnSemanticTypeToFormatterType = (column: IOgqlV1Column): keyof ILocaleFormatter => {
  const allSemanticTypes = getAllSemanticTypes(column);

  if (isNumericColumn(column)) {
    switch (true) {
      case allSemanticTypes.has(BasicColumnTypes.Currency):
      case allSemanticTypes.has(SemanticTypes.StructuredDataType.MonetaryAmount):
      case allSemanticTypes.has(SemanticTypes.StructuredDataType.MonetaryAmountHttps):
        return BasicColumnTypes.Currency;
      case allSemanticTypes.has(BasicColumnTypes.Percent):
        return 'percent';
      default:
        // For anything else we return number, including 'fiscal/year'
        return 'number';
    }
  }
  if (column?.logicalType?.includes('logical/date')) {
    return 'date';
  }
  return 'currency';
};
